import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import logoWithName from '../../images/TapTabLogoWhiteSmallWithName.png';
import restaurantImage from '../../assets/images/BalthazarChampagneBar.png';
import TapTabLogoPlaceholder from '../../assets/images/TapTabLogoPlaceholder.png';
import 'react-loading-skeleton/dist/skeleton.css';
import { Dropdown } from '../common/Form';
import { isRestaurantDropdownHidden } from './sidebarNavigation';
import SidebarNavLinks from './SidebarNavLinks';
import { useBrandContext } from '../../contexts/BrandContext';
import { PAGE_BRAND_PROFILE } from '../../constants/UriConstants';

const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { brands, currentBrand, setBrand, isLoading } = useBrandContext();

  const handleBrandSelected = (brandID: string) => {
    setBrand(brandID);
    navigate(`/${brandID}/brand-profile`);
  };

  const imageOnErrorHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // eslint-disable-next-line no-param-reassign
    event.currentTarget.src = restaurantImage;
  };

  const getBrandImage = () => {
    if (brands?.length === 0 && !isLoading) {
      return <img src={TapTabLogoPlaceholder} alt="TapTab placeholder" />;
    }

    if (!currentBrand || isLoading) {
      return <Skeleton circle width="90px" height="90px" baseColor="#333333" highlightColor="#444444" />;
    }

    if (currentBrand.logoUrl) {
      return (
        <img
          onError={imageOnErrorHandler}
          src={currentBrand.logoUrl}
          className="sidebar-restaurant-icon"
          alt="Brand logo"
        />
      );
    }

    return <img src={restaurantImage} className="sidebar-restaurant-icon" alt="Brand" />;
  };

  const getBrandName = () => {
    if (brands?.length === 0 && !isLoading) {
      return null;
    }

    if (!currentBrand || isLoading) {
      return <Skeleton width="66%" baseColor="#333333" highlightColor="#444444" />;
    }

    if (brands?.length > 1 && !isRestaurantDropdownHidden(pathname)) {
      return (
        <Dropdown
          className="sidebar-restaurant-dropdown"
          onChange={handleBrandSelected}
          value={currentBrand.id}
          values={brands?.map((brand) => ({
            label: brand.name,
            value: brand.id
          }))}
        />
      );
    }

    return <div className="sidebar-restaurant-name">{currentBrand.name}</div>;
  };

  const getBrandID = () => {
    if (brands?.length === 0 && !isLoading) {
      return <div className="sidebar-restaurant-id">Profile</div>;
    }

    if (!currentBrand || isLoading) {
      return <Skeleton width="33%" baseColor="#333333" highlightColor="#444444" />;
    }

    return <div className="sidebar-restaurant-id">{currentBrand.id.substring(0, 6)}</div>;
  };

  const handleLogoClicked = () => {
    if (currentBrand) {
      navigate(`/${currentBrand.id}/${PAGE_BRAND_PROFILE}`);
    } else {
      navigate('/');
    }
  };

  const handleLogoKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.code === 'Enter' || event.code === 'Space') {
      if (currentBrand) {
        navigate(`/${currentBrand.id}/${PAGE_BRAND_PROFILE}`);
      } else {
        navigate('/');
      }
    }
  };

  return (
    <div className="sidebar-container">
      <div className="sidebar-header-container">
        <div
          role="button"
          onClick={handleLogoClicked}
          onKeyPress={handleLogoKeyPress}
          tabIndex={0}
          className="sidebar-logo-container"
        >
          <img src={logoWithName} className="sidebar-logo" alt="TapTab logo" />
        </div>
        <div className="sidebar-profile-container">
          <div className="sidebar-image-container">{getBrandImage()}</div>
          <div className="sidebar-info-container">
            {getBrandName()}
            {getBrandID()}
          </div>
        </div>
      </div>
      <SidebarNavLinks />
      <div hidden>Version {process.env.REACT_APP_VERSION}</div>
    </div>
  );
};

export default Sidebar;
